import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import type { Socket } from 'socket.io-client';
import {
  AmbulanceQueueRow,
  getAmbulanceQueueBookingById,
  getAmbulanceCurrentTrackingLocation,
  getAmbulanceTrackingHistory,
  AmbulanceTrackingPoint,
} from '../../services/MasterAdmin/ambulanceQueueService';
import {
  connectAmbulanceTrackingSocket,
  joinBookingTrackingSocket,
  leaveBookingTrackingSocket,
  registerMasterAdminSocket,
  type AmbulanceLocationPayload,
} from '../../services/MasterAdmin/ambulanceTrackingSocket';
import { getDirections } from '../../services/googleMapsService';
import { REACT_APP_GOOGLE_MAPS_API_KEY } from '../../config/env';
import { decodePolyline, latLngToParam, parseLatLng, type LatLngLiteral } from '../../utils/mapLocation';
import {
  AMBULANCE_MARKER_ICON,
  DROP_MARKER_ICON,
  PICKUP_MARKER_ICON,
} from '../../utils/ambulanceMapMarkers';

const DEFAULT_CENTER: LatLngLiteral = { lat: 20.5937, lng: 78.9629 };
const REST_POLL_MS = 10_000;

function applyLocationPayload(
  data: AmbulanceLocationPayload,
  bookingId: string,
  onUpdate: (lat: number, lng: number, updatedAt: string | null) => void
) {
  if (data.bookingId && String(data.bookingId) !== bookingId) return;
  const lat = Number(data.latitude);
  const lng = Number(data.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
  onUpdate(lat, lng, data.timestamp ?? new Date().toISOString());
}

const AmbulanceQueueTrackingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bookingId = '' } = useParams<{ bookingId: string }>();
  const queueRowFromState = (location.state as { row?: AmbulanceQueueRow } | null)?.row ?? null;

  const [queueRow, setQueueRow] = useState<AmbulanceQueueRow | null>(queueRowFromState);
  const [bookingLoading, setBookingLoading] = useState(true);
  const [trackingLoading, setTrackingLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [trackingError, setTrackingError] = useState<string | null>(null);
  const [statusHint, setStatusHint] = useState<string | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [trackingActive, setTrackingActive] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [latest, setLatest] = useState<{
    latitude: number | null;
    longitude: number | null;
    updatedAt?: string | null;
  } | null>(null);
  const [history, setHistory] = useState<AmbulanceTrackingPoint[]>([]);
  const [routePaths, setRoutePaths] = useState<{
    ambulanceToPickup: LatLngLiteral[];
    pickupToDrop: LatLngLiteral[];
  }>({ ambulanceToPickup: [], pickupToDrop: [] });

  const timerRef = useRef<number | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const socketRef = useRef<Socket | null>(null);

  const mapsApiKey = REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'ambulance-queue-track-map',
    googleMapsApiKey: mapsApiKey,
  });

  const pickupMarker = useMemo(
    () => parseLatLng(queueRow?.pickupLocation),
    [queueRow?.pickupLocation]
  );
  const dropMarker = useMemo(() => parseLatLng(queueRow?.dropLocation), [queueRow?.dropLocation]);

  const currentMarker = useMemo((): LatLngLiteral | null => {
    if (latest?.latitude == null || latest?.longitude == null) return null;
    const lat = Number(latest.latitude);
    const lng = Number(latest.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [latest]);

  const trailPath = useMemo(() => {
    const ordered = [...history].sort(
      (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
    );
    return ordered
      .filter((p) => Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)))
      .map((p) => ({ lat: Number(p.latitude), lng: Number(p.longitude) }));
  }, [history]);

  const latestDriverId = useMemo(
    () => history.find((h) => h.driverId)?.driverId || queueRow?.assignedDriverId || null,
    [history, queueRow?.assignedDriverId]
  );

  const center =
    currentMarker || pickupMarker || dropMarker || trailPath[trailPath.length - 1] || DEFAULT_CENTER;

  const fitMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;
    const points: LatLngLiteral[] = [
      ...trailPath,
      ...routePaths.ambulanceToPickup,
      ...routePaths.pickupToDrop,
    ];
    if (currentMarker) points.push(currentMarker);
    if (pickupMarker) points.push(pickupMarker);
    if (dropMarker) points.push(dropMarker);
    if (!points.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((p) => bounds.extend(p));
    mapRef.current.fitBounds(bounds, 48);
  }, [trailPath, routePaths, currentMarker, pickupMarker, dropMarker]);

  const applyLiveCoords = useCallback((lat: number, lng: number, updatedAt: string | null) => {
    setLatest({ latitude: lat, longitude: lng, updatedAt });
    setTrackingActive(true);
    setStatusHint(null);
    setTrackingError(null);
  }, []);

  const loadBooking = useCallback(async () => {
    if (!bookingId) return;
    try {
      setBookingLoading(true);
      setBookingError(null);
      const res = await getAmbulanceQueueBookingById(bookingId);
      if (res?.data) {
        setQueueRow(res.data);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      setBookingError(err?.response?.data?.message || 'Failed to load booking details.');
      if (queueRowFromState) setQueueRow(queueRowFromState);
    } finally {
      setBookingLoading(false);
    }
  }, [bookingId, queueRowFromState]);

  const loadTracking = useCallback(
    async (silent = false) => {
      if (!bookingId) return;
      try {
        if (!silent) setTrackingLoading(true);
        else setRefreshing(true);
        setTrackingError(null);

        const [currentRes, historyRes] = await Promise.all([
          getAmbulanceCurrentTrackingLocation(bookingId),
          getAmbulanceTrackingHistory(bookingId, { limit: 300, order: 'desc' }),
        ]);

        const logs = Array.isArray(historyRes?.data?.logs) ? historyRes.data.logs : [];
        const latestLog = logs.length ? logs[0] : null;
        const hasCurrentLocation =
          Number.isFinite(Number(currentRes?.data?.latitude)) &&
          Number.isFinite(Number(currentRes?.data?.longitude));

        const isActive = Boolean(currentRes?.data?.trackingActive);
        setTrackingActive(isActive);
        setLatest({
          latitude: hasCurrentLocation
            ? Number(currentRes?.data?.latitude)
            : latestLog?.latitude ?? null,
          longitude: hasCurrentLocation
            ? Number(currentRes?.data?.longitude)
            : latestLog?.longitude ?? null,
          updatedAt: currentRes?.data?.updatedAt ?? latestLog?.recordedAt ?? null,
        });
        setHistory(logs);

        if (!isActive && !hasCurrentLocation && !latestLog) {
          setStatusHint(
            'Driver has not started live tracking. Open the trip in the driver/owner app and tap Start Trip.'
          );
        } else if (isActive && !hasCurrentLocation && !latestLog) {
          setStatusHint('Tracking started — waiting for first GPS update.');
        } else {
          setStatusHint(null);
        }
      } catch (e: unknown) {
        const err = e as { response?: { data?: { message?: string } } };
        setTrackingError(
          err?.response?.data?.message || 'Could not load live GPS (booking map still shown).'
        );
      } finally {
        setTrackingLoading(false);
        setRefreshing(false);
      }
    },
    [bookingId]
  );

  const loadRoute = useCallback(async () => {
    if (!pickupMarker || !dropMarker) return;
    try {
      const origin = currentMarker
        ? latLngToParam(currentMarker)
        : latLngToParam(pickupMarker);
      const res = await getDirections({
        origin,
        pickup: latLngToParam(pickupMarker),
        drop: latLngToParam(dropMarker),
      });
      if (!res?.success) return;

      const ambulanceToPickup = res.data.originToPickup?.polyline
        ? decodePolyline(res.data.originToPickup.polyline)
        : [];
      const pickupToDrop = res.data.pickupToDrop?.polyline
        ? decodePolyline(res.data.pickupToDrop.polyline)
        : [];

      setRoutePaths({ ambulanceToPickup, pickupToDrop });
    } catch {
      setRoutePaths({ ambulanceToPickup: [], pickupToDrop: [] });
    }
  }, [pickupMarker, dropMarker, currentMarker]);

  useEffect(() => {
    void loadBooking();
    void loadTracking(false);
  }, [loadBooking, loadTracking]);

  useEffect(() => {
    void loadRoute();
  }, [loadRoute]);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      void loadTracking(true);
    }, REST_POLL_MS);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [loadTracking]);

  useEffect(() => {
    if (!bookingId) return undefined;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    const socket = connectAmbulanceTrackingSocket(token);
    socketRef.current = socket;

    const onLocation = (data: AmbulanceLocationPayload) => {
      applyLocationPayload(data, bookingId, applyLiveCoords);
    };
    const onStarted = (data: { bookingId?: string }) => {
      if (data?.bookingId && String(data.bookingId) !== bookingId) return;
      setTrackingActive(true);
      setStatusHint(null);
    };
    const onEnded = (data: { bookingId?: string }) => {
      if (data?.bookingId && String(data.bookingId) !== bookingId) return;
      setTrackingActive(false);
      setStatusHint('Live tracking ended.');
    };

    socket.on('connect', () => setSocketConnected(true));
    socket.on('disconnect', () => setSocketConnected(false));
    socket.on('ambulanceLocationUpdate', onLocation);
    socket.on('ambulanceTrackingStarted', onStarted);
    socket.on('ambulanceTrackingEnded', onEnded);

    let cancelled = false;
    void (async () => {
      const registered = await registerMasterAdminSocket(socket, token);
      if (cancelled || !registered.success) return;
      const joined = await joinBookingTrackingSocket(socket, bookingId);
      if (cancelled || !joined.success) return;
      if (joined.trackingActive) setTrackingActive(true);
      const loc = joined.currentLocation;
      if (
        loc &&
        Number.isFinite(Number(loc.latitude)) &&
        Number.isFinite(Number(loc.longitude))
      ) {
        applyLiveCoords(
          Number(loc.latitude),
          Number(loc.longitude),
          loc.updatedAt ?? new Date().toISOString()
        );
      }
    })();

    return () => {
      cancelled = true;
      leaveBookingTrackingSocket(socket, bookingId);
      socket.off('ambulanceLocationUpdate', onLocation);
      socket.off('ambulanceTrackingStarted', onStarted);
      socket.off('ambulanceTrackingEnded', onEnded);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [bookingId, applyLiveCoords]);

  useEffect(() => {
    if (isLoaded) fitMap();
  }, [isLoaded, fitMap]);

  const pickupIcon = useMemo(() => (isLoaded ? PICKUP_MARKER_ICON() : undefined), [isLoaded]);
  const dropIcon = useMemo(() => (isLoaded ? DROP_MARKER_ICON() : undefined), [isLoaded]);
  const ambulanceIcon = useMemo(() => (isLoaded ? AMBULANCE_MARKER_ICON() : undefined), [isLoaded]);

  const mapBlocked =
    !mapsApiKey || Boolean(loadError) || (bookingLoading && !pickupMarker && !dropMarker);

  return (
    <div className="w-full min-w-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate('/request-queue?tab=ambulance')}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to request queue
        </button>
        <button
          type="button"
          onClick={() => {
            void loadBooking();
            void loadTracking(true);
            void loadRoute();
          }}
          disabled={refreshing}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Ambulance live tracking</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Booking ID: <span className="font-mono">{bookingId}</span>
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <span
            className={`inline-flex rounded-full px-2 py-1 font-semibold ${
              trackingActive
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
            }`}
          >
            {trackingActive ? 'Tracking active' : 'Tracking inactive'}
          </span>
          <span
            className={`inline-flex rounded-full px-2 py-1 font-semibold ${
              socketConnected
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            {socketConnected ? 'Live socket connected' : 'Socket connecting…'}
          </span>
          {pickupMarker && dropMarker ? (
            <span className="inline-flex rounded-full bg-emerald-100 px-2 py-1 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
              Route loaded on map
            </span>
          ) : (
            <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-red-800 dark:bg-red-900/30 dark:text-red-300">
              Missing pickup/drop coordinates
            </span>
          )}
          {latest?.updatedAt ? (
            <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              Last GPS: {new Date(latest.updatedAt).toLocaleString()}
            </span>
          ) : null}
        </div>
        {statusHint ? (
          <p className="mt-2 text-sm text-amber-700 dark:text-amber-300">{statusHint}</p>
        ) : null}
        {trackingError ? (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{trackingError}</p>
        ) : null}
        {bookingError ? (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{bookingError}</p>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="relative h-[72vh] min-h-[480px] w-full">
          {mapBlocked ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-sm text-gray-500 dark:text-gray-400">
              {!mapsApiKey ? (
                <p>
                  Missing <code className="rounded bg-gray-100 px-1 dark:bg-gray-700">REACT_APP_GOOGLE_MAPS_API_KEY</code>{' '}
                  in admin env.
                </p>
              ) : loadError ? (
                <p>Google Maps failed to load: {loadError.message}</p>
              ) : (
                <p>Loading booking route…</p>
              )}
            </div>
          ) : !isLoaded ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              Loading Google Maps…
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={center}
              zoom={13}
              onLoad={(map) => {
                mapRef.current = map;
                fitMap();
              }}
            >
              {routePaths.pickupToDrop.length > 1 ? (
                <Polyline
                  path={routePaths.pickupToDrop}
                  options={{ strokeColor: '#7C3AED', strokeOpacity: 0.7, strokeWeight: 5 }}
                />
              ) : null}
              {routePaths.ambulanceToPickup.length > 1 ? (
                <Polyline
                  path={routePaths.ambulanceToPickup}
                  options={{ strokeColor: '#3B82F6', strokeOpacity: 0.85, strokeWeight: 5 }}
                />
              ) : null}
              {trailPath.length > 1 ? (
                <Polyline
                  path={trailPath}
                  options={{ strokeColor: '#2563EB', strokeOpacity: 0.5, strokeWeight: 3 }}
                />
              ) : null}
              {pickupMarker ? (
                <Marker
                  position={pickupMarker}
                  icon={pickupIcon}
                  title="Pickup"
                  zIndex={3}
                />
              ) : null}
              {dropMarker ? (
                <Marker position={dropMarker} icon={dropIcon} title="Drop" zIndex={3} />
              ) : null}
              {currentMarker ? (
                <Marker
                  position={currentMarker}
                  icon={ambulanceIcon}
                  title="Ambulance (live)"
                  zIndex={5}
                />
              ) : null}
            </GoogleMap>
          )}

          {trackingLoading && !mapBlocked && isLoaded ? (
            <div className="pointer-events-none absolute left-3 top-3 rounded-md bg-white/90 px-2 py-1 text-xs text-gray-600 shadow dark:bg-gray-900/90 dark:text-gray-300">
              Updating live GPS…
            </div>
          ) : null}

          {!mapBlocked && isLoaded && !currentMarker ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center px-4">
              <p className="max-w-md rounded-lg bg-white/95 px-4 py-2 text-center text-sm text-gray-700 shadow dark:bg-gray-900/95 dark:text-gray-200">
                Pickup and drop are on the map. Live ambulance appears when the driver starts the trip and GPS is sent.
              </p>
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setDetailsOpen((v) => !v)}
            className="absolute right-3 top-3 z-10 inline-flex items-center gap-1 rounded-md bg-white/95 px-2.5 py-1.5 text-xs font-medium text-gray-800 shadow hover:bg-white dark:bg-gray-800/95 dark:text-gray-100"
          >
            {detailsOpen ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
            {detailsOpen ? 'Hide details' : 'Show details'}
          </button>

          {detailsOpen ? (
            <aside className="absolute right-3 top-14 z-10 w-[300px] max-w-[calc(100%-24px)] rounded-lg bg-white/95 p-3 text-xs shadow-lg backdrop-blur dark:bg-gray-900/95">
              <h2 className="mb-2 text-sm font-semibold text-gray-900 dark:text-white">Booking details</h2>
              <div className="mb-2 space-y-1 text-gray-600 dark:text-gray-300">
                <p>
                  <span className="font-semibold text-green-700 dark:text-green-400">●</span> Pickup marker
                </p>
                <p>
                  <span className="font-semibold text-red-600 dark:text-red-400">●</span> Drop marker
                </p>
                <p>
                  <span className="font-semibold text-orange-500">●</span> Ambulance (live GPS)
                </p>
                <p>
                  <span className="font-semibold text-violet-600">—</span> Road route pickup → drop
                </p>
                <p>
                  <span className="font-semibold text-blue-600">—</span> Road route ambulance → pickup
                </p>
              </div>
              <div className="space-y-2 text-gray-700 dark:text-gray-200">
                <div>
                  <p className="font-semibold">User</p>
                  <p>{queueRow?.userName || '—'}</p>
                  <p className="text-gray-500 dark:text-gray-400">{queueRow?.userPhone || '—'}</p>
                </div>
                <div>
                  <p className="font-semibold">Owner (Ambulance agency)</p>
                  <p>{queueRow?.acceptedByVendorName || '—'}</p>
                </div>
                <div>
                  <p className="font-semibold">Driver</p>
                  <p>{queueRow?.assignedDriverName || '—'}</p>
                  <p className="text-gray-500 dark:text-gray-400">
                    {queueRow?.assignedDriverPhone || latestDriverId || '—'}
                  </p>
                </div>
                <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                  <p className="font-semibold">Route</p>
                  <p className="line-clamp-2">{queueRow?.pickupAddress || '—'}</p>
                  <p className="line-clamp-2">{queueRow?.dropAddress || '—'}</p>
                </div>
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AmbulanceQueueTrackingPage;
